/**
 * The Clerk dashboard, Form 4 name approvals and council authorizations over
 * gov-api.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClerkDashboard,
  CouncilAuthorizationView,
  DecideCouncilAuthorizationDto,
  DecideForm4NameApprovalDto,
  FileCouncilAuthorizations,
  FileForm4NameApprovals,
  Form4NameApprovalView,
  RequestCouncilAuthorizationDto,
  RequestForm4NameApprovalDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { filesKeys } from './files-api';
import { useMutationToast } from './mutation-toast';

export const clerkKeys = {
  all: ['clerk'] as const,
  dashboard: (localGovernmentId: string) => ['clerk', 'dashboard', localGovernmentId] as const,
  form4: (fileId: string) => ['clerk', 'form4', fileId] as const,
  council: (fileId: string) => ['clerk', 'council', fileId] as const,
};

export async function getClerkDashboard(localGovernmentId: string): Promise<ClerkDashboard> {
  const { data } = await api.get<ClerkDashboard>(`/clerk-dashboard/${localGovernmentId}`);
  return data;
}

export async function getForm4NameApprovals(fileId: string): Promise<FileForm4NameApprovals> {
  const { data } = await api.get<FileForm4NameApprovals>(`/files/${fileId}/form4-name-approvals`);
  return data;
}

export async function getCouncilAuthorizations(fileId: string): Promise<FileCouncilAuthorizations> {
  const { data } = await api.get<FileCouncilAuthorizations>(
    `/files/${fileId}/council-authorizations`,
  );
  return data;
}

export function useClerkDashboard(localGovernmentId: string | null) {
  return useQuery({
    queryKey: clerkKeys.dashboard(localGovernmentId ?? ''),
    queryFn: () => getClerkDashboard(localGovernmentId ?? ''),
    enabled: Boolean(localGovernmentId),
  });
}

export function useForm4NameApprovals(fileId: string) {
  return useQuery({
    queryKey: clerkKeys.form4(fileId),
    queryFn: () => getForm4NameApprovals(fileId),
  });
}

export function useCouncilAuthorizations(fileId: string) {
  return useQuery({
    queryKey: clerkKeys.council(fileId),
    queryFn: () => getCouncilAuthorizations(fileId),
  });
}

/** Everything a decision on one file can change: its panels, its trail, every dashboard. */
function useInvalidate() {
  const qc = useQueryClient();
  return (fileId: string) => {
    void qc.invalidateQueries({ queryKey: clerkKeys.form4(fileId) });
    void qc.invalidateQueries({ queryKey: clerkKeys.council(fileId) });
    void qc.invalidateQueries({ queryKey: ['clerk', 'dashboard'] });
    void qc.invalidateQueries({ queryKey: filesKeys.activity(fileId) });
  };
}

export function useRequestForm4NameApproval(fileId: string) {
  const invalidate = useInvalidate();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (dto: RequestForm4NameApprovalDto) =>
      (await api.post<Form4NameApprovalView>(`/files/${fileId}/form4-name-approvals`, dto)).data,
    onSuccess: () => {
      invalidate(fileId);
      toast.success('gov.clerk.form4.requested');
    },
    onError: (err) => toast.error(err, 'gov.clerk.form4.requestFailed'),
  });
}

export function useWithdrawForm4NameApproval(fileId: string) {
  const invalidate = useInvalidate();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<Form4NameApprovalView>(
          `/files/${fileId}/form4-name-approvals/${id}/withdraw`,
        )
      ).data,
    onSuccess: () => {
      invalidate(fileId);
      toast.success('gov.clerk.withdrawn');
    },
    onError: (err) => toast.error(err, 'gov.clerk.actionFailed'),
  });
}

export function useDecideForm4NameApproval() {
  const invalidate = useInvalidate();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async ({
      fileId,
      id,
      dto,
    }: {
      fileId: string;
      id: string;
      dto: DecideForm4NameApprovalDto;
    }) =>
      (
        await api.post<Form4NameApprovalView>(
          `/files/${fileId}/form4-name-approvals/${id}/decision`,
          dto,
        )
      ).data,
    onSuccess: (_data, vars) => {
      invalidate(vars.fileId);
      toast.success(
        vars.dto.decision === 'approve' ? 'gov.clerk.form4.approved' : 'gov.clerk.form4.declined',
      );
    },
    onError: (err) => toast.error(err, 'gov.clerk.actionFailed'),
  });
}

export function useRequestCouncilAuthorization(fileId: string) {
  const invalidate = useInvalidate();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (dto: RequestCouncilAuthorizationDto) =>
      (await api.post<CouncilAuthorizationView>(`/files/${fileId}/council-authorizations`, dto))
        .data,
    onSuccess: () => {
      invalidate(fileId);
      toast.success('gov.clerk.council.requested');
    },
    onError: (err) => toast.error(err, 'gov.clerk.council.requestFailed'),
  });
}

export function useWithdrawCouncilAuthorization(fileId: string) {
  const invalidate = useInvalidate();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<CouncilAuthorizationView>(
          `/files/${fileId}/council-authorizations/${id}/withdraw`,
        )
      ).data,
    onSuccess: () => {
      invalidate(fileId);
      toast.success('gov.clerk.withdrawn');
    },
    onError: (err) => toast.error(err, 'gov.clerk.actionFailed'),
  });
}

export function useDecideCouncilAuthorization() {
  const invalidate = useInvalidate();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async ({
      fileId,
      id,
      dto,
    }: {
      fileId: string;
      id: string;
      dto: DecideCouncilAuthorizationDto;
    }) =>
      (
        await api.post<CouncilAuthorizationView>(
          `/files/${fileId}/council-authorizations/${id}/decision`,
          dto,
        )
      ).data,
    onSuccess: (_data, vars) => {
      invalidate(vars.fileId);
      toast.success(
        vars.dto.decision === 'authorize'
          ? 'gov.clerk.council.authorized'
          : 'gov.clerk.council.declined',
      );
    },
    onError: (err) => toast.error(err, 'gov.clerk.actionFailed'),
  });
}
